import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import Swal from "sweetalert2";
import "../styles/Results.css";

/** الدومين الأساسي لتوليد رابط الإحالة */
const SURVEY_BASE_URL = "https://survey.matajeralhai.com/";

/** استخراج كود الإحالة من نص (قد يكون رابط كامل أو كود صِرف) */
const extractCode = (line) => {
  if (!line) return "";
  const trimmed = line.trim();
  // لو لصق رابط كامل: https://.../?ref=MH001
  try {
    const url = new URL(trimmed);
    const ref = url.searchParams.get("ref");
    if (ref) return ref.trim();
  } catch (e) {
    // مش رابط: اعتبره كود مباشر
  }
  // لو سطر زي: MH001, فلان -> خذ أول كلمة غير فاضية
  const maybeCode = trimmed.split(/[,\s]/).filter(Boolean)[0] || "";
  return maybeCode.trim();
};

export default function ReferralManager() {
  // أكواد من الردود لاكتشاف أي أكواد ظهرت حتى لو مش متسجلة في referrals
  const [codesFromSurveys, setCodesFromSurveys] = useState([]);
  // خريطة الأكواد من كوليكشن referrals: { CODE: { ownerName, updatedAt, ... } }
  const [referralMap, setReferralMap] = useState({});
  // اختيارات الفورم
  const [selectedCode, setSelectedCode] = useState("");
  const [ownerName, setOwnerName] = useState("");
  // إدخال جماعي
  const [bulkText, setBulkText] = useState("");

  /** الاشتراك في كوليكشن survey_form لاستخراج الأكواد التي ظهرت فعليًا */
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "survey_form"), (snap) => {
      const setCodes = new Set();
      snap.forEach((d) => {
        const ref = (d.data()?.referral || "").toString().trim();
        if (ref) setCodes.add(ref);
      });
      setCodesFromSurveys(Array.from(setCodes));
    });
    return () => unsub();
  }, []);

  /** الاشتراك في كوليكشن referrals (دفتر الأكواد) */
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "referrals"), (snap) => {
      const map = {};
      snap.forEach((d) => (map[d.id] = d.data()));
      setReferralMap(map);
    });
    return () => unsub();
  }, []);

  /** كل الأكواد المتاحة للدروب داون: اتحاد (من referrals + من الردود) بدون تكرار */
  const referralCodesAll = useMemo(() => {
    const fromMap = Object.keys(referralMap);
    const union = new Set([...fromMap, ...codesFromSurveys]);
    return Array.from(union).sort();
  }, [referralMap, codesFromSurveys]);

  /** عند اختيار كود، عبّي اسم صاحبه إن موجود */
  const onSelectCode = (code) => {
    setSelectedCode(code);
    const current = (referralMap[code]?.ownerName || "").toString();
    setOwnerName(current);
  };

  /** حفظ صاحب الكود في referrals (Doc ID = code) */
  const saveOne = async () => {
    const cleanCode = (selectedCode || "").trim();
    const cleanOwner = (ownerName || "").trim();
    if (!cleanCode) {
      Swal.fire("تنبيه", "اختَر/اكتب كود الإحالة أولًا", "info");
      return;
    }
    await setDoc(
      doc(db, "referrals", cleanCode),
      {
        ownerName: cleanOwner,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    Swal.fire("تم الحفظ", "تم ربط الكود بصاحبه", "success");
  };

  /** نسخ رابط الإحالة */
  const copyReferralLink = async () => {
    const cleanCode = (selectedCode || "").trim();
    if (!cleanCode) {
      Swal.fire("تنبيه", "اختَر/اكتب كود أولًا لنسخ الرابط", "info");
      return;
    }
    const url = `${SURVEY_BASE_URL}?ref=${encodeURIComponent(cleanCode)}`;
    try {
      await navigator.clipboard.writeText(url);
      Swal.fire("تم النسخ", "تم نسخ رابط الإحالة إلى الحافظة", "success");
    } catch (e) {
      window.prompt("انسخ الرابط:", url);
    }
  };

  /** إدخال جماعي من Textarea (سطر لكل كود أو رابط) */
  const seedFromText = async () => {
    const lines = (bulkText || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) {
      Swal.fire("تنبيه", "لا توجد أسطر صالحة للإدخال", "info");
      return;
    }
    const codes = lines.map((l) => extractCode(l)).filter(Boolean);
    if (!codes.length) {
      Swal.fire("تنبيه", "لم يتم التعرف على أي كود", "info");
      return;
    }
    const confirmation = await Swal.fire({
      title: "تأكيد الإدخال الجماعي",
      text: `سيتم إنشاء/تحديث ${codes.length} كود.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#7EBFE9",
      cancelButtonColor: "#aaa",
      confirmButtonText: "متابعة",
      cancelButtonText: "إلغاء",
    });
    if (!confirmation.isConfirmed) return;

    const batch = writeBatch(db);
    codes.forEach((c) => {
      batch.set(
        doc(db, "referrals", c),
        {
          ownerName: referralMap[c]?.ownerName || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    });
    await batch.commit();
    setBulkText("");
    Swal.fire("تم", "تم إدخال الأكواد من القائمة.", "success");
  };

  return (
    <div className="results-container" dir="rtl">
      <h1 className="results-title">إدارة روابط الإحالة</h1>

      {/* فورم الإدارة (بنفس ستايل الفلاتر) */}
      <div className="results-filters" style={{ marginTop: "0.8rem" }}>
        <div className="filter-item" style={{ minWidth: 260 }}>
          <label>كود الإحالة (اختَر من الموجود أو اكتُب جديد)</label>
          <select
            value={selectedCode}
            onChange={(e) => onSelectCode(e.target.value)}
          >
            <option value="">— اختر كودًا —</option>
            {referralCodesAll.map((c) => {
              const owner = (referralMap[c]?.ownerName || "").toString().trim();
              return (
                <option key={c} value={c}>
                  {c} {owner ? `— ${owner}` : "— غير معيّن"}
                </option>
              );
            })}
          </select>
        </div>

        <div className="filter-item" style={{ minWidth: 260 }}>
          <label>أو اكتُب كود جديد</label>
          <input
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            placeholder="مثال: MH021 أو أي كود"
            autoComplete="off"
          />
        </div>

        <div className="filter-item" style={{ minWidth: 260 }}>
          <label>اسم صاحب الإحالة</label>
          <input
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="اسم الشخص"
            autoComplete="off"
          />
        </div>

        <button
          className="download-btn"
          style={{ marginTop: "2.1rem" }}
          onClick={saveOne}
        >
          حفظ
        </button>
        <button
          className="download-btn"
          style={{ marginTop: "2.1rem", background: "var(--primary)" }}
          onClick={copyReferralLink}
        >
          نسخ الرابط
        </button>
      </div>

      {/* جدول صغير: كود/رابط الإحالة + صاحب الإحالة */}
      <div className="table-responsive" style={{ marginTop: "1rem" }}>
        <table className="results-table">
          <thead>
            <tr>
              <th>كود/رابط الإحالة</th>
              <th>صاحب الإحالة</th>
            </tr>
          </thead>
          <tbody>
            {referralCodesAll.length ? (
              referralCodesAll.map((code) => {
                const owner = (referralMap[code]?.ownerName || "").toString();
                const url = `${SURVEY_BASE_URL}?ref=${encodeURIComponent(
                  code
                )}`;
                return (
                  <tr key={code}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          justifyContent: "center",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{code}</span>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="page-link"
                          style={{ textDecoration: "none" }}
                          title="فتح الرابط في تبويب جديد"
                        >
                          فتح الرابط
                        </a>
                        <button
                          className="page-link"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(url);
                              Swal.fire(
                                "تم النسخ",
                                "تم نسخ رابط الإحالة إلى الحافظة",
                                "success"
                              );
                            } catch (e) {
                              window.prompt("انسخ الرابط:", url);
                            }
                          }}
                          title="نسخ رابط الإحالة"
                        >
                          نسخ الرابط
                        </button>
                      </div>
                    </td>
                    <td>{owner || "—"}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={2} className="text-center text-muted">
                  لا توجد أكواد بعد
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
